/**
 * Geometry for the sign-in globe — deliberately dependency-free.
 *
 * The globe is a *dot* globe: continents are a stipple of points sampled from
 * real coastlines (`dots.ts`), projected orthographically onto a shaded
 * sphere and drawn only where they face the viewer. So the geometry needed
 * here is small — read the coastline rings out of the GeoJSON, and turn a
 * geographic coordinate into a unit vector the renderer can rotate.
 *
 * (An earlier pass ray-cast a photographic planet through this module —
 * per-pixel inverse rotation, equirectangular texture lookup, a day/night
 * terminator. That direction was dropped when the portal was aligned with the
 * customer support portal's dot-globe design, and the machinery went with it.
 * A dot field has no back-face problem to solve: each dot is one point, and a
 * point either faces the camera or it does not.)
 *
 * Frame convention (right-handed, camera at +z looking back at the origin):
 *
 *   x → right, y → up (north pole at +y before tilt), z → toward the viewer.
 *   A point is on the visible hemisphere iff `z >= 0`.
 */

/** A coastline ring as raw `[lng0,lat0, lng1,lat1, …]` degrees.
 *
 * Degrees, not unit vectors, because the only consumer is the point-in-ring
 * land test in `dots.ts`, whose axes *are* longitude and latitude —
 * converting to vectors and back would just add error. Flat because a ring of
 * 5 000 points costs one allocation this way instead of 5 000. */
export type LngLatRing = Float32Array;

/** The shape we accept from `/data/ne_110m_land.geojson`. Kept structural
 * (not a full GeoJSON type) because the loader validates at runtime — the
 * file is fetched, so TypeScript can promise nothing about it. */
type LooseGeoJson = {
  type?: unknown;
  features?: unknown;
};

/**
 * Pull every ring out of a land GeoJSON.
 *
 * Returns `[]` — never throws — for anything that is not a usable
 * FeatureCollection. The globe is decoration on a sign-in screen: if the
 * coastline file 404s, or a stubbed `fetch` in a test hands back an error
 * body, the right outcome is an oceans-only planet, not a blank page.
 */
export function extractRings(data: unknown): LngLatRing[] {
  const doc = data as LooseGeoJson | null;
  if (!doc || typeof doc !== "object" || doc.type !== "FeatureCollection") return [];
  if (!Array.isArray(doc.features)) return [];

  const rings: LngLatRing[] = [];
  const pushPolygon = (poly: unknown) => {
    if (!Array.isArray(poly)) return;
    for (const ring of poly) {
      if (!Array.isArray(ring) || ring.length < 4) continue;
      const flat: number[] = [];
      for (const pt of ring) {
        if (!Array.isArray(pt) || typeof pt[0] !== "number" || typeof pt[1] !== "number") continue;
        flat.push(pt[0], pt[1]);
      }
      if (flat.length >= 8) rings.push(Float32Array.from(flat));
    }
  };

  for (const feature of doc.features) {
    const geometry = (feature as { geometry?: { type?: unknown; coordinates?: unknown } } | null)
      ?.geometry;
    if (!geometry) continue;
    if (geometry.type === "Polygon") pushPolygon(geometry.coordinates);
    else if (geometry.type === "MultiPolygon" && Array.isArray(geometry.coordinates)) {
      for (const poly of geometry.coordinates) pushPolygon(poly);
    }
  }
  return rings;
}

/** A direction in the frame described above. */
export type Vec3 = readonly [number, number, number];

/** Unit vector for a geographic coordinate, in the frame described above. */
export function latLngToVec(latDeg: number, lngDeg: number): Vec3 {
  const phi = (latDeg * Math.PI) / 180;
  const theta = (lngDeg * Math.PI) / 180;
  const c = Math.cos(phi);
  return [c * Math.sin(theta), Math.sin(phi), c * Math.cos(theta)];
}

/** Spin a direction about the polar axis (which face of the planet is toward
 * the camera). */
export function rotY(v: Vec3, angle: number): Vec3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [v[0] * c + v[2] * s, v[1], -v[0] * s + v[2] * c];
}

/** Tilt a direction toward or away from the camera (the viewer sitting
 * slightly above the equator). */
export function rotX(v: Vec3, angle: number): Vec3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [v[0], v[1] * c - v[2] * s, v[1] * s + v[2] * c];
}

/**
 * Spherical interpolation between two directions — the great-circle path a
 * packet takes between two network nodes. Degrades to `a` when the two are
 * (near-)identical, where the great circle is undefined and the correct
 * answer is "don't move".
 */
export function slerp(a: Vec3, b: Vec3, t: number): Vec3 {
  const dotRaw = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const dot = dotRaw < -1 ? -1 : dotRaw > 1 ? 1 : dotRaw;
  const omega = Math.acos(dot);
  const sinO = Math.sin(omega);
  if (sinO < 1e-4) return a;
  const w1 = Math.sin((1 - t) * omega) / sinO;
  const w2 = Math.sin(t * omega) / sinO;
  return [a[0] * w1 + b[0] * w2, a[1] * w1 + b[1] * w2, a[2] * w1 + b[2] * w2];
}
